import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Package, Truck, CheckCircle, Clock, AlertTriangle, CreditCard, Loader2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { PaymentModal } from '../../components/shop/PaymentModal';
import { supabase } from '../../lib/supabase';
import { getCustomerProfileByToken } from '../../services/customerProfileService';
import { useTenantContext } from '../../context/TenantContext';

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface ProductImage {
  image_url: string;
}

interface Product {
  title: string;
  price: number;
  images?: ProductImage[];
}

interface OrderItem {
  id: number;
  order_id: number;
  product_id: number;
  quantity: number;
  price: number;
  price_at_purchase: number;
  title?: string;
  image_url?: string;
  product?: Product;
}

interface Order {
  id: number;
  order_number: string;
  created_at: string;
  status: string;
  total_amount: number;
  shipping_address: string;
  customer_profile_id: number;
  items: OrderItem[];
}

const OrderDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();
  
  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'Package', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerProfile, setCustomerProfile] = useState<any>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  
  // Fonction pour récupérer les détails de la commande
  const fetchOrderDetails = async () => {
    if (!id) return;
    
    try {
      // Récupérer les détails de la commande
      const { data: orderData, error: orderError } = await supabase
        .from('shop_orders')
        .select('*')
        .eq('id', id)
        .single();
      
      if (orderError) throw orderError;
      
      if (orderData) {
        // Récupérer les articles de la commande avec les détails des produits et images
        const { data: itemsData, error: itemsError } = await supabase
          .from('shop_order_items')
          .select(`
            *,
            product:product_id(title, price, images:shop_product_images(image_url))
          `)
          .eq('order_id', orderData.id);
        
        if (itemsError) throw itemsError;
        
        // Mettre à jour les données de la commande
        setOrder({
          ...orderData,
          items: itemsData || []
        });
        
        // Si le nom du client n'est pas défini (utilisateur non authentifié),
        // utiliser le nom du client depuis la commande
        if (!customerName && orderData.customer_name) {
          setCustomerName(orderData.customer_name);
        }
      } else {
        toast({
          title: 'Commande introuvable',
          description: 'Impossible de trouver les détails de cette commande',
          variant: 'destructive'
        });
        navigate('/boutique/commandes');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des détails de la commande:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer les détails de la commande',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Vérifier si l'utilisateur est authentifié (optionnel)
  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('customer_access_token');
      if (token) {
        try {
          const profile = await getCustomerProfileByToken(token);
          if (profile) {
            setIsAuthenticated(true);
            setCustomerProfile(profile);
            setCustomerName(profile.full_name || profile.email.split('@')[0]);
            setCustomerEmail(profile.email);
          } else {
            // Token invalide ou expiré
            localStorage.removeItem('customer_access_token');
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          localStorage.removeItem('customer_access_token');
        }
      }
      
      // Récupérer les détails de la commande que l'utilisateur soit authentifié ou non
      if (id) fetchOrderDetails();
      else setIsLoading(false);
    };
    
    checkAuthentication();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate, toast]);
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'shipped': return <Truck className="h-5 w-5 text-purple-500" />;
      case 'processing': return <Package className="h-5 w-5 text-blue-500" />;
      case 'paid': return <CreditCard className="h-5 w-5 text-green-500" />;
      case 'pending_payment': return <Clock className="h-5 w-5 text-yellow-500" />;
      default: return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };
  
  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Commande terminée';
      case 'shipped': return 'Commande expédiée';
      case 'processing': return 'En cours de traitement';
      case 'paid': return 'Commande payée';
      case 'pending_payment': return 'En attente de paiement';
      default: return 'En attente';
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-300';
      case 'shipped': return 'bg-purple-500/20 text-purple-300';
      case 'processing': return 'bg-blue-500/20 text-blue-300';
      case 'paid': return 'bg-green-500/20 text-green-300';
      case 'pending_payment': return 'bg-yellow-500/20 text-yellow-300';
      default: return 'bg-gray-500/20 text-gray-300';
    }
  };
  
  // Fonction pour payer une commande
  const handlePayOrder = async () => {
    if (!order) return;
    
    try {
      setIsLoading(true);
      
      // Ouvrir la modale de paiement avec les détails de la commande
      setSelectedOrder({
        id: order.id,
        order_number: order.order_number,
        total_amount: order.total_amount,
        customer_name: customerName,
        customer_email: customerEmail
      });
      setIsPaymentModalOpen(true);
      
    } catch (error) {
      console.error('Erreur lors de la préparation du paiement:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de procéder au paiement. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fonction appelée après un paiement réussi
  const handlePaymentSuccess = () => {
    // Fermer la modale
    setIsPaymentModalOpen(false);
    
    // Rafraîchir les détails de la commande
    if (id) fetchOrderDetails();
    
    // Afficher un message de succès
    toast({
      title: 'Paiement réussi',
      description: 'Votre commande a été payée avec succès.',
      variant: 'default'
    });
  };
  
  return (
    <PublicLayout>
      <SEOMetaTags 
        title="Détails de la commande" 
        description="Consultez les informations détaillées de votre commande."
      />

      <div className="text-white relative overflow-hidden min-h-screen pt-20">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.Package;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>
        <div className="relative z-10 container mx-auto px-4 py-12 max-w-2xl">
          <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/boutique/commandes')}
                className="mr-4 backdrop-blur-sm transition-colors"
                style={{ 
                  color: tenant?.config?.color4 || '#9ca3af',
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                  e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                  e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = tenant?.config?.color4 || '#9ca3af';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour
              </Button>
              <h1 className="text-2xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Détails de la commande</h1>
          </div>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: tenant?.config?.color1 || '#fe8d17' }}></div>
            </div>
          ) : order ? (
            <Card className="bg-black/20 text-white backdrop-blur-sm"
              style={{
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-xl">Commande #{order.order_number}</CardTitle>
                  <CardDescription className="text-white/70">
                    Passée le {new Date(order.created_at).toLocaleDateString('fr-FR')} à {new Date(order.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </CardDescription>
                </div>
                <div className="flex items-center mb-4">
                  <Badge className={`${getStatusColor(order.status)} mr-2`}>
                    {getStatusIcon(order.status)}
                  </Badge>
                  <span className="font-medium">{getStatusText(order.status)}</span>
                </div>
                
                {/* Bouton de paiement pour les commandes en attente */}
                {(order.status === 'pending_payment') && (
                  <Button 
                    className="mb-4 shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                    onClick={handlePayOrder}
                    disabled={isLoading}
                    style={{
                      backgroundColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color3 || '#ffffff',
                      backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                      boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                    }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Chargement...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Payer maintenant
                      </>
                    )}
                  </Button>
                )}
              </CardHeader>
              
              <CardContent className="space-y-6">
                <div className="bg-white/5 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Articles</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 px-2">Image</th>
                          <th className="text-left py-2 px-2">Produit</th>
                          <th className="text-center py-2 px-2">Qté</th>
                          <th className="text-right py-2 px-2">Prix unitaire</th>
                          <th className="text-right py-2 px-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item) => {
                          // Déterminer l'URL de l'image
                          const imageUrl = item.product?.images?.[0]?.image_url || item.image_url;
                          // Déterminer le titre du produit
                          const productTitle = item.product?.title || item.title || 'Produit';
                          // Déterminer le prix (utiliser price_at_purchase qui est toujours défini)
                          const price = item.price_at_purchase || 0;
                          
                          return (
                            <tr key={item.id} className="border-b border-white/5">
                              <td className="py-3 px-2 w-16">
                                {imageUrl ? (
                                  <img 
                                    src={imageUrl} 
                                    alt={productTitle} 
                                    className="w-12 h-12 object-cover rounded-md"
                                  />
                                ) : (
                                  <div className="w-12 h-12 bg-white/10 rounded-md flex items-center justify-center">
                                    <Package className="h-6 w-6 text-white/50" />
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-2">
                                <p className="font-medium">{productTitle}</p>
                              </td>
                              <td className="py-3 px-2 text-center">{item.quantity}</td>
                              <td className="py-3 px-2 text-right">
                                <p className="font-medium">
                                  {new Intl.NumberFormat('fr-FR').format(price)} FCFA
                                </p>
                              </td>
                              <td className="py-3 px-2 text-right">
                                <p className="font-semibold">
                                  {new Intl.NumberFormat('fr-FR').format(price * item.quantity)} FCFA
                                </p>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <div className="bg-white/5 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3">Récapitulatif</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <p className="text-white/70">Sous-total</p>
                      <p>{new Intl.NumberFormat('fr-FR').format(order.total_amount)} FCFA</p>
                    </div>
                    <div className="flex justify-between">
                      <p className="text-white/70">Frais de livraison</p>
                      <p>Gratuit</p>
                    </div>
                    <Separator className="my-2 bg-white/10" />
                    <div className="flex justify-between font-semibold">
                      <p>Total</p>
                      <p>{new Intl.NumberFormat('fr-FR').format(order.total_amount)} FCFA</p>
                    </div>
                  </div>
                </div>
                
                {order.shipping_address && (
                  <div className="bg-white/5 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-3">Adresse de livraison</h3>
                    {(() => {
                      try {
                        // Essayer de parser l'adresse si c'est une chaîne JSON
                        const addressObj = typeof order.shipping_address === 'string' 
                          ? JSON.parse(order.shipping_address) 
                          : order.shipping_address;
                        
                        // Si c'est un objet, formater l'adresse correctement
                        if (typeof addressObj === 'object' && addressObj !== null) {
                          return (
                            <div className="space-y-1">
                              {addressObj.address && <p>{addressObj.address}</p>}
                              {addressObj.city && (
                                <p>
                                  {addressObj.postal_code ? `${addressObj.postal_code} ` : ''}
                                  {addressObj.city}
                                </p>
                              )}
                              {addressObj.country && <p>{addressObj.country}</p>}
                            </div>
                          );
                        }
                        
                        // Fallback si ce n'est pas un objet valide
                        return <p className="whitespace-pre-line">{String(order.shipping_address)}</p>;
                      } catch (e) {
                        // En cas d'erreur de parsing, afficher tel quel
                        return <p className="whitespace-pre-line">{String(order.shipping_address)}</p>;
                      }
                    })()} 
                  </div>
                )}
              </CardContent>
              
              <CardFooter className="flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/boutique/commandes')}
                  className="transition-all duration-200"
                  style={{
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                    color: tenant?.config?.color1 || '#fe8d17'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                    e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                  }}
                >
                  Retour aux commandes
                </Button>
                <Button className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                  onClick={() => navigate('/boutique')}
                  style={{
                    backgroundColor: tenant?.config?.color1 || '#fe8d17',
                    color: tenant?.config?.color3 || '#ffffff',
                    backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                    boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                  }}
                >
                  Continuer mes achats
                </Button>
              </CardFooter>
            </Card>
          ) : !isAuthenticated ? (
            <Card className="bg-black/20 text-white backdrop-blur-sm"
              style={{
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
              }}
            >
              <CardHeader>
                <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Accès aux détails de commande</CardTitle>
                <CardDescription style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                  Connectez-vous pour accéder aux détails de votre commande
                </CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="text-center py-8">
                  <p className="mb-4" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                    Vous devez vous connecter pour accéder aux détails de cette commande.
                  </p>
                  <Button onClick={() => navigate('/boutique/commandes')} className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                    style={{
                      backgroundColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color3 || '#ffffff',
                      backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                      boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                    }}
                  >
                    Aller à la page de connexion
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
              <h2 className="text-xl font-semibold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Commande introuvable</h2>
              <p className="mb-4" style={{ color: tenant?.config?.color4 || '#9ca3af' }}>
                Impossible de trouver les détails de cette commande
              </p>
              <Button 
                onClick={() => {
                  // Utiliser state pour préserver l'authentification
                  if (isAuthenticated && customerProfile) {
                    navigate('/boutique/commandes', { state: { preserveAuth: true } });
                  } else {
                    navigate('/boutique/commandes');
                  }
                }}
                className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                style={{
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                  boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                }}
              >
                Retour aux commandes
              </Button>
            </div>
          )}
          </motion.div>
        </div>
      </div>
      
      {/* Modale de paiement */}
      {selectedOrder && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          order={selectedOrder}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </PublicLayout>
  );
};

export default OrderDetailsPage;
